import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Lock } from 'lucide-react';
import type { Process } from '@/types';

export function ProcessDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { appUser, canAccessCompany, canWriteCompanyData } = useAuth();
  const [process, setProcess] = useState<Process | null>(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleActivateProcess = async () => {
    if (!id || !process) return;
    if (!window.confirm('¿Activar este proceso? Los candidatos podrán recibir invitaciones y responder evaluaciones.')) return;
    setActivating(true);
    setError(null);
    try {
      await updateDoc(doc(db, 'processes', id), {
        status: 'active',
        updatedAt: new Date().toISOString(),
      });
      setProcess((prev) => (prev ? { ...prev, status: 'active' } : null));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setActivating(false);
    }
  };

  const handleCloseProcess = async () => {
    if (!id || !process) return;
    if (
      !window.confirm(
        '¿Estás seguro de finalizar este proceso? Los candidatos ya no podrán responder evaluaciones ni se podrán enviar nuevas invitaciones.'
      )
    )
      return;
    setClosing(true);
    setError(null);
    try {
      await updateDoc(doc(db, 'processes', id), {
        status: 'closed',
        updatedAt: new Date().toISOString(),
      });
      setProcess((prev) => (prev ? { ...prev, status: 'closed' } : null));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setClosing(false);
    }
  };

  useEffect(() => {
    if (!id || !appUser) return;
    const load = async () => {
      const snap = await getDoc(doc(db, 'processes', id));
      if (!snap.exists()) {
        setProcess(null);
        setLoading(false);
        return;
      }
      const data = snap.data();
      if (!canAccessCompany(data.companyId)) {
        setProcess(null);
        setLoading(false);
        return;
      }
      setProcess({
        id: snap.id,
        ...data,
        createdAt: data.createdAt ?? '',
        updatedAt: data.updatedAt ?? '',
      } as Process);
      setLoading(false);
    };
    load();
  }, [id, appUser, canAccessCompany]);

  if (loading) return <p className="text-muted-foreground">Cargando...</p>;
  if (!process) return <p className="text-muted-foreground">Proceso no encontrado.</p>;

  const isClosed = process.status === 'closed';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/app/processes">← Procesos</Link>
        </Button>
      </div>
      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle>{process.title}</CardTitle>
            {process.status === 'active' && (
              <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                Activo
              </Badge>
            )}
            {process.status === 'closed' && (
              <Badge variant="secondary" className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                Finalizado
              </Badge>
            )}
            {process.status === 'draft' && (
              <Badge variant="outline">Borrador</Badge>
            )}
          </div>
          {process.description && (
            <p className="text-sm text-muted-foreground">{process.description}</p>
          )}
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {canWriteCompanyData && process.status === 'draft' && (
            <Button
              size="sm"
              onClick={handleActivateProcess}
              disabled={activating}
            >
              {activating ? 'Activando...' : 'Activar Proceso'}
            </Button>
          )}
          {canWriteCompanyData && process.status === 'active' && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleCloseProcess}
              disabled={closing}
            >
              <Lock className="mr-1.5 h-4 w-4" />
              {closing ? 'Finalizando...' : 'Finalizar Proceso'}
            </Button>
          )}
          {canWriteCompanyData && !isClosed && (
            <Button asChild>
              <Link to={`/app/processes/${id}/battery`}>Configurar batería</Link>
            </Button>
          )}
          <Button variant="outline" asChild>
            <Link to={`/app/processes/${id}/candidates`}>Candidatos</Link>
          </Button>
          {canWriteCompanyData && !isClosed && (
            <Button variant="outline" asChild>
              <Link to={`/app/processes/${id}/candidates/invite`}>Invitar candidato</Link>
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
